import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Search, Plus, Eye, Download, Printer } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const CreditNotes: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    creditNotes,
    patients,
    walkInClients,
    invoices
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const filteredCreditNotes = creditNotes.filter(cn => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (cn.creditNoteNumber.toLowerCase().includes(query)) return true;
    let customerName = '';
    if (cn.customerType === 'Patient') {
      const p = patients.find(p => p.id === cn.customerId);
      if (p) customerName = `${p.firstName} ${p.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === cn.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(query);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Issued':
        return 'bg-blue-100 text-blue-800';
      case 'Applied':
        return 'bg-green-100 text-green-800';
      case 'Refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-971">
      <div className="flex justify-between items-center" data-id="element-972">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-973">Credit Notes</h1>
        <Button onClick={() => onNavigate('new-credit-note' as any)} data-id="element-974">
          <Plus className="w-4 h-4 mr-2" data-id="element-975" /> New Credit Note
        </Button>
      </div>

      <Card noPadding data-id="element-976">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-977">
          <div className="relative max-w-md" data-id="element-978">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-979">
              <Search className="h-5 w-5 text-gray-400" data-id="element-980" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by credit note number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-981" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-982">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-983">
            <thead className="bg-gray-50" data-id="element-984">
              <tr data-id="element-985">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-986">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-987">
                  Credit Note#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-988">
                  Invoice#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-989">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-990">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-991">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-992">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-993">
              {filteredCreditNotes.length > 0 ? filteredCreditNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(cn => {
              const inv = invoices.find(i => i.id === cn.invoiceId);
              return <tr key={cn.id} className="hover:bg-gray-50" data-id="element-994">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-995">
                          {new Date(cn.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-996">
                          {cn.creditNoteNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-997">
                          {inv?.invoiceNumber || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-998">
                          {getCustomerName(cn.customerId, cn.customerType)}
                          <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-999">
                            ({cn.customerType})
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1000">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(cn.status)}`} data-id="element-1001">
                            {cn.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1002">
                          {formatCurrency(cn.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1003">
                          <button className="text-gray-400 hover:text-blue-600 mx-1" title="View" data-id="element-1004">
                            <Eye className="w-4 h-4" data-id="element-1005" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600 mx-1" title="Print" data-id="element-1006">
                            <Printer className="w-4 h-4" data-id="element-1007" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600 mx-1" title="Download" data-id="element-1008">
                            <Download className="w-4 h-4" data-id="element-1009" />
                          </button>
                        </td>
                      </tr>;
            }) : <tr data-id="element-1010">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1011">
                    No credit notes found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};